import { useCallback, useState, useEffect } from "react";
import { evolveBadges } from "@/lib/badges";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import api, { BACKEND_URL } from "@/lib/api";
import { getProfilePhotoUrl, getCoverUrl, optimizeImage } from "@/lib/mediaUrls";
import { toast } from "sonner";
import FeaturedBusinessesCarousel from "@/components/FeaturedBusinessesCarousel";

const GENRES = [
  "All Genres",
  "Hip Hop",
  "Rap",
  "Trap",
  "R&B",
  "Pop",
  "Drill",
  "Afrobeats",
  "Dancehall",
  "Reggae",
  "Alternative",
  "Other"
];

// Badge component
const Badge = ({ type }) => {
  const badges = {
    founder: { label: "Founder", icon: "👑", className: "bg-gradient-to-r from-yellow-400 to-amber-600 text-black" },
    founding_artist: { label: "Founding Artist", icon: "⭐", className: "bg-gradient-to-r from-purple-500 to-pink-600 text-white" },
    artist: { label: "Artist", icon: "🎤", className: "bg-gray-700 text-white" },
    producer: { label: "Producer", icon: "🎧", className: "bg-blue-600 text-white" },
    spotlight_winner: { label: "Spotlight", icon: "🏆", className: "bg-gradient-to-r from-green-500 to-emerald-600 text-white" },
    admin: { label: "Admin", icon: "⚡", className: "bg-purple-600 text-white" }
  };
  const badge = badges[type];
  if (!badge) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${badge.className}`}>
      <span>{badge.icon}</span>
      {badge.label}
    </span>
  );
};

// Artist Card Component
const ArtistCard = ({ artist, currentUser, onFollow }) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(artist.follower_count || 0);
  const [followLoading, setFollowLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && artist.followers) {
      setIsFollowing(artist.followers.includes(currentUser.user_id));
    }
  }, [currentUser, artist.followers]);

  const handleFollow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUser) {
      navigate('/login');
      return;
    }

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await api.delete(`/artist/${artist.user_id}/follow?follower_id=${currentUser.user_id}`);
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
      } else {
        await api.post(`/artist/${artist.user_id}/follow`, { follower_id: currentUser.user_id });
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (error) {
      console.error("Follow error:", error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (artist.tracks && artist.tracks.length > 0) {
      navigate(`/track/${artist.tracks[0].submission_id}`);
    } else {
      navigate(`/artist/${artist.user_id}`);
    }
  };

  const getDisplayName = () => artist.artist_name || artist.name || 'Artist';
  const getProfilePhoto = () => getProfilePhotoUrl(artist);

  const isOwnProfile = currentUser?.user_id === artist.user_id;

  return (
    <Link to={`/artist/${artist.user_id}`}>
      <Card className="p-4 bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/50 transition-all group" data-testid={`artist-card-${artist.user_id}`}>
        <div className="flex items-center gap-4">
          {/* Profile Photo */}
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-purple-500/30">
              <img
                src={optimizeImage(getProfilePhoto(), 'avatar')}
                alt={getDisplayName()}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop'; }}
              />
            </div>
            {/* Play button overlay */}
            {artist.track_count > 0 && (
              <button
                onClick={handlePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </button>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white truncate">{getDisplayName()}</h3>
            <div className="flex flex-wrap gap-1 mt-1">
              {evolveBadges(artist.badges)?.slice(0, 2).map(badge => (
                <Badge key={badge} type={badge} />
              ))}
            </div>
            <div className="flex items-center gap-3 mt-1 text-gray-500 text-sm">
              <span>{followerCount} followers</span>
              {artist.track_count > 0 && <span>{artist.track_count} tracks</span>}
            </div>
          </div>

          {/* Follow Button */}
          {!isOwnProfile && (
            <Button
              onClick={handleFollow}
              disabled={followLoading}
              size="sm"
              className={`flex-shrink-0 ${isFollowing 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
              }`}
              data-testid="follow-btn"
            >
              {followLoading ? '...' : isFollowing ? 'Following' : 'Follow'}
            </Button>
          )}
        </div>
      </Card>
    </Link>
  );
};

const ExplorePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
  const [selectedGenre, setSelectedGenre] = useState(searchParams.get('genre') || 'All Genres');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchResults, setSearchResults] = useState(null);
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [trendingArtists, setTrendingArtists] = useState([]);
  const [newArtists, setNewArtists] = useState([]);
  const [producers, setProducers] = useState([]);
  // Patch A follow-up (Feb 2026) — genre-filtered artist results live in
  // their own state so they never masquerade as canonical Featured
  // Artists (which come exclusively from /api/discover/featured).
  const [genreFilteredArtists, setGenreFilteredArtists] = useState([]);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      setCurrentUser(null);
    }
  }, []);

  const fetchDiscoveryData = useCallback(async () => {
    setLoading(true);
    try {
      const [featured, trending, newRes, producersRes] = await Promise.all([
        api.get('/discover/featured').catch(() => ({ data: { artists: [] } })),
        api.get('/discover/trending').catch(() => ({ data: { artists: [] } })),
        api.get('/discover/new').catch(() => ({ data: { artists: [] } })),
        api.get('/discover/producers').catch(() => ({ data: { artists: [] } }))
      ]);

      setFeaturedArtists(featured.data.artists || []);
      setTrendingArtists(trending.data.artists || []);
      setNewArtists(newRes.data.artists || []);
      setProducers(producersRes.data.artists || []);
    } catch (error) {
      console.error("Failed to fetch discovery data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFilteredArtists = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/discover/artists?genre=${selectedGenre}`);
      // Write genre-filtered results into their OWN state — never
      // overwrite the canonical Featured Artists state, which is
      // sourced only from /api/discover/featured.
      setGenreFilteredArtists(response.data.artists || []);
      setFeaturedArtists([]);
      setTrendingArtists([]);
      setNewArtists([]);
      setProducers([]);
    } catch (error) {
      console.error("Failed to fetch filtered artists:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedGenre]);

  const performSearch = useCallback(async () => {
    try {
      const response = await api.get(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search failed:", error);
    }
  }, [searchQuery]);

  useEffect(() => {
    checkAuth();
    fetchDiscoveryData();
  }, [checkAuth, fetchDiscoveryData]);

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    if (activeTab !== 'all') params.set('tab', activeTab);
    if (selectedGenre !== 'All Genres') params.set('genre', selectedGenre);
    if (searchQuery) params.set('q', searchQuery);
    setSearchParams(params);
  }, [activeTab, selectedGenre, searchQuery, setSearchParams]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      performSearch();
    } else {
      setSearchResults(null);
    }
  }, [searchQuery, performSearch]);

  useEffect(() => {
    if (selectedGenre !== 'All Genres') {
      fetchFilteredArtists();
    } else {
      fetchDiscoveryData();
    }
  }, [selectedGenre, fetchFilteredArtists, fetchDiscoveryData]);

  const renderArtistSection = (title, artists, emptyMessage) => {
    if (!artists || artists.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          {emptyMessage || "No artists found"}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {artists.map(artist => (
          <ArtistCard 
            key={artist.user_id} 
            artist={artist} 
            currentUser={currentUser}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="explore-page">
      {/* Header */}
      <nav className="fixed left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-gray-800" style={{ top: "var(--rgm-app-banner-h, 0px)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img src="/rgm-logo.png" alt="RGM Studio" className="h-10 w-auto object-contain" />
            </Link>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-xl mx-8">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <Input
                  type="text"
                  placeholder="Search artists, tracks, producers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-900/50 border-gray-700 text-white w-full"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-gray-400 hover:text-white">
                  Home
                </Button>
              </Link>
              {currentUser ? (
                <Link to="/dashboard">
                  <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500">
                    Join as Artist
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-4xl font-black text-white mb-2">Explore Artists</h1>
            <p className="text-gray-400">Discover talented independent artists and producers on RGM Studio</p>
          </div>

          {/* Search Results */}
          {searchResults && searchQuery.length >= 2 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">
                Search Results for "{searchQuery}"
              </h2>
              
              {/* Artists */}
              {searchResults.artists?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-purple-400 mb-4">Artists</h3>
                  {renderArtistSection(null, searchResults.artists)}
                </div>
              )}
              
              {/* Tracks */}
              {searchResults.tracks?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-purple-400 mb-4">Tracks</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {searchResults.tracks.map(track => (
                      <Link key={track.submission_id} to={`/track/${track.submission_id}`}>
                        <Card className="p-4 bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/50 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 relative">
                              <img
                                src={getCoverUrl(track)}
                                alt={track.track_title}
                                className="w-full h-full object-cover"
                              />
                              {track.status === 'featured' && (
                                <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-bl-lg px-1">
                                  <span className="text-xs" title="Featured">🎖️</span>
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-bold text-white truncate">{track.track_title || 'Untitled'}</h4>
                                {track.status === 'featured' && (
                                  <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-yellow-400 to-amber-500 text-black" data-testid="track-featured-badge">
                                    Featured
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-400 text-sm truncate">{track.artist_name}</p>
                              <p className="text-gray-600 text-xs">{track.genre}</p>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Producers */}
              {searchResults.producers?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-purple-400 mb-4">Producers</h3>
                  {renderArtistSection(null, searchResults.producers)}
                </div>
              )}
              
              {(!searchResults.artists?.length && !searchResults.tracks?.length && !searchResults.producers?.length) && (
                <div className="text-center py-12 text-gray-500">
                  No results found for "{searchQuery}"
                </div>
              )}
            </div>
          )}

          {/* Genre Filters */}
          {!searchQuery && (
            <>
              <div className="mb-8 overflow-x-auto">
                <div className="flex gap-2 pb-2">
                  {GENRES.map(genre => (
                    <button
                      key={genre}
                      onClick={() => setSelectedGenre(genre)}
                      className={`px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                        selectedGenre === genre
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                      }`}
                      data-testid={`genre-filter-${genre.toLowerCase().replace(' ', '-')}`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="flex gap-4 mb-8 border-b border-gray-800">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'featured', label: 'Featured' },
                  { key: 'trending', label: 'Trending' },
                  { key: 'new', label: 'New Artists' },
                  { key: 'producers', label: 'Producers' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`pb-4 px-2 text-sm font-medium transition-all border-b-2 ${
                      activeTab === tab.key
                        ? 'text-purple-400 border-purple-400'
                        : 'text-gray-500 border-transparent hover:text-gray-300'
                    }`}
                    data-testid={`tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
                </div>
              ) : (
                <>
                  {/* Featured Businesses (Phase 6) */}
                  {activeTab === 'all' && <FeaturedBusinessesCarousel variant="explore" />}

                  {/* Genre-filtered artists — labeled as such, NOT as Featured.
                      Renders only when a specific genre is selected. */}
                  {selectedGenre !== 'All Genres' && genreFilteredArtists.length > 0 && (
                    <section className="mb-12" data-testid="genre-filtered-section">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                          <span className="text-2xl">🎧</span> Artists in {selectedGenre}
                        </h2>
                      </div>
                      {renderArtistSection(null, genreFilteredArtists, `No artists found in ${selectedGenre}`)}
                    </section>
                  )}

                  {/* Featured Artists — canonical, only in All Genres mode. */}
                  {selectedGenre === 'All Genres' && (activeTab === 'all' || activeTab === 'featured') && featuredArtists.length > 0 && (
                    <section className="mb-12" data-testid="featured-artists-section">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                          <span className="text-2xl">⭐</span> Featured Artists
                        </h2>
                        {activeTab === 'all' && featuredArtists.length > 6 && (
                          <button 
                            onClick={() => setActiveTab('featured')}
                            className="text-purple-400 hover:text-purple-300 text-sm"
                          >
                            View All →
                          </button>
                        )}
                      </div>
                      {renderArtistSection(null, activeTab === 'all' ? featuredArtists.slice(0, 6) : featuredArtists, "No featured artists yet")}
                    </section>
                  )}

                  {/* Trending Artists */}
                  {(activeTab === 'all' || activeTab === 'trending') && trendingArtists.length > 0 && selectedGenre === 'All Genres' && (
                    <section className="mb-12">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                          <span className="text-2xl">🔥</span> Trending Artists
                        </h2>
                        {activeTab === 'all' && trendingArtists.length > 6 && (
                          <button 
                            onClick={() => setActiveTab('trending')}
                            className="text-purple-400 hover:text-purple-300 text-sm"
                          >
                            View All →
                          </button>
                        )}
                      </div>
                      {renderArtistSection(null, activeTab === 'all' ? trendingArtists.slice(0, 6) : trendingArtists, "No trending artists yet")}
                    </section>
                  )}

                  {/* New Artists */}
                  {(activeTab === 'all' || activeTab === 'new') && newArtists.length > 0 && selectedGenre === 'All Genres' && (
                    <section className="mb-12">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                          <span className="text-2xl">✨</span> New Artists
                        </h2>
                        {activeTab === 'all' && newArtists.length > 6 && (
                          <button 
                            onClick={() => setActiveTab('new')}
                            className="text-purple-400 hover:text-purple-300 text-sm"
                          >
                            View All →
                          </button>
                        )}
                      </div>
                      {renderArtistSection(null, activeTab === 'all' ? newArtists.slice(0, 6) : newArtists, "No new artists yet")}
                    </section>
                  )}

                  {/* Producers */}
                  {(activeTab === 'all' || activeTab === 'producers') && producers.length > 0 && selectedGenre === 'All Genres' && (
                    <section className="mb-12">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                          <span className="text-2xl">🎧</span> Producers
                        </h2>
                        {activeTab === 'all' && producers.length > 6 && (
                          <button 
                            onClick={() => setActiveTab('producers')}
                            className="text-purple-400 hover:text-purple-300 text-sm"
                          >
                            View All →
                          </button>
                        )}
                      </div>
                      {renderArtistSection(null, activeTab === 'all' ? producers.slice(0, 6) : producers, "No producers yet. Are you a producer? Update your profile!")}
                    </section>
                  )}

                  {/* Empty State */}
                  {featuredArtists.length === 0 && trendingArtists.length === 0 && newArtists.length === 0 && producers.length === 0 && genreFilteredArtists.length === 0 && (
                    <div className="text-center py-20">
                      <div className="text-6xl mb-4">🎵</div>
                      <h3 className="text-2xl font-bold text-white mb-2">No Artists Found</h3>
                      <p className="text-gray-400 mb-6">
                        {selectedGenre !== 'All Genres' 
                          ? `No artists found in ${selectedGenre}. Try another genre!`
                          : "Be one of the first artists to join RGM Studio!"}
                      </p>
                      {!currentUser && (
                        <Link to="/login">
                          <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500">
                            Join as Artist
                          </Button>
                        </Link>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity">
            <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain" />
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default ExplorePage;
